import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  SCROLL_WORKSPACE,
  ScrollWorkspaceAction,
  UPLOAD_LANE,
  UploadLaneAction,
  ClearAlertAction,
  CLEAR_ALERT,
  ResetStoreAction,
  RESET_STORE,
  SET_MOUSE_TIME,
  SetMouseTimeAction
} from "../../actions/actionTypes";
import { AlertMessage, EditorMode, MainStore } from "../../store";
import { bpm_to_seconds_per_measure } from "../../util/quantization";
import {
  pixels_to_second,
  seconds_to_pixels
} from "../../util/time_position_conversion";
import { getZoomProportion } from "../../util/zoom_levels";
import { AddLane } from "../AddLane";
import { AddLaneModal } from "../AddLaneModal";
import { TimeTicks } from "../TimeTicks";
import { Locator } from "../TimeTicks/Locator";
import { LaneHeaders } from "../LaneHeaders";
import { Lane } from "../Lane";
import {
  Container,
  LeftPanelContainer,
  GridContainer,
  LanesAreaContainer,
  MarkerOverlay
} from "./styles";
import { SnapControls } from "../SnapControls";
import { Header } from "../Header";
import { ZoomControls } from "../ZoomControls";
import { MessageContext } from "../../../context/MessageContext";
import { DragDropAudioOverlay } from "../DragDropAudioOverlay";
import useModal from "../../../hooks/useModal";
import { EditorContext } from "../../context/EditorContext/EditorProvider";
import { theme } from "../../../globalStyles";
import { InOutMarkers } from "../TimeTicks/InOutMarkers";
import { AuthContext } from "../../../context/AuthProvider";
import { WaitMessageOverlay } from "../WaitMessageOverlay";

export let Editor = () => {
  const SCREEN_COVERAGE_SNAP_PROPORTION = 0.825;

  const editor = useContext(EditorContext);
  const user = useContext(AuthContext);

  const [dragActive, setDragActive] = useState(false);

  const [naturalDrawerHeight, setNaturalDrawerHeight] = useState(
    parseInt(theme.editor.editorHeaderHeightNoPx)
  );
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth
  });

  const addLaneModal = useModal();
  const waitMessageModal = useModal();

  const bpm = useSelector<MainStore, number>(
    state => state.project.initialTrackData.bpm
  );
  const beatsPerMeasure = useSelector<MainStore, number>(
    state => state.project.initialTrackData.beatsPerMeasure
  );
  const inMarkerTime = useSelector<MainStore, number>(
    state => state.project.markers.in.songTime
  );
  const outMarkerTime = useSelector<MainStore, number>(
    state => state.project.markers.out.songTime
  );
  const seconds_per_measure = bpm_to_seconds_per_measure(bpm, beatsPerMeasure);
  const pixels_per_measure = seconds_to_pixels(seconds_per_measure);
  const zoomLevel = useSelector<MainStore, number>(
    state => state.uiState.zoomLevel
  );
  const zoomProportion = getZoomProportion(zoomLevel);

  const { showMessage, closeMessage } = useContext(MessageContext);
  const alert = useSelector<MainStore, AlertMessage>(
    state => state.uiState.alert
  );

  const waitMessage = useSelector<MainStore, string>(
    state => state.uiState.waitMessage?.message
  );

  const dispatch = useDispatch();
  const laneIds = useSelector<MainStore, string[]>(
    state => state.project.lanes.laneIds
  );
  let className = "lanes-area";

  const mode = useSelector<MainStore, EditorMode>(
    state => state.editorState.mode
  );
  if (mode === EditorMode.SPLIT) {
    className += " split-mode";
  }

  const onScroll = e => {
    dispatch<ScrollWorkspaceAction>({
      type: SCROLL_WORKSPACE,
      x: e.currentTarget.scrollLeft
    });
  };

  const handleDrag = function (e) {
    if (addLaneModal.visible) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = event => {
    if (addLaneModal.visible) return;
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    uploadAudio(event);
  };

  const uploadAudio = event => {
    const fileList: FileList = event.target.files || event.dataTransfer.files;
    if (fileList.length === 0) {
      return;
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      try {
        const isWav = file.name.toLowerCase().endsWith(".wav");
        const isMp3 = file.name.toLowerCase().endsWith(".mp3");

        if (user.user.variationEditor) {
          if (!isWav) {
            throw Error(
              `file ${file.name} has invalid type, only .wav files are allowed.`
            );
          }
        } else {
          if (!isWav && !isMp3) {
            throw Error(
              `file ${file.name} has invalid type, not a .mp3 or .wav`
            );
          }
        }

        const url = URL.createObjectURL(file);
        const fileName = file.name.replace(".wav", "").replace(".mp3", "");
        dispatch<UploadLaneAction>({
          type: UPLOAD_LANE,
          url,
          name: fileName,
          instruments: editor.instruments
        });
      } catch (error) {
        closeMessage();
        showMessage({
          type: "error",
          duration: 5000,
          title: "Add Audio Fail",
          description:
            error.message ||
            "Error loading audio file. Please check that it is a valid .wav or .mp3 file."
        });
      }
    }
  };

  const inBetweenBarLines = pixels_per_measure * zoomProportion;
  const zoomInitialOffset = inBetweenBarLines;

  const drawerContainer = document.querySelector<HTMLElement>(
    ".editor-drawer .ant-drawer-content-wrapper"
  );

  /*
   * Snap the editor drawer to the top of the screen
   *
   * Params:
   *  screenCoverageProportion (number): [0,1] only snap if editor drawer covers at least this proportion of the screen
   */
  const snapEditorDrawerToTop = (screenCoverageProportion = 0) => {
    if (drawerContainer === null) return;
    const drawerAlmostFillsScreen =
      naturalDrawerHeight / window.innerHeight >= screenCoverageProportion;
    if (drawerAlmostFillsScreen) {
      drawerContainer.style.height = "100%";
    } else {
      drawerContainer.style.height = `${naturalDrawerHeight}px`;
    }
  };

  const unsnapEditorDrawerFromTop = () => {
    if (drawerContainer === null) return;
    drawerContainer.style.height = `${naturalDrawerHeight}px`;
  };

  const toggleSnapEditorDrawerTop = () => {
    if (drawerContainer === null) return;
    const isExpanded = drawerContainer.style.height === "100%";
    if (isExpanded) {
      unsnapEditorDrawerFromTop();
    } else {
      snapEditorDrawerToTop();
    }
  };

  // this hook clears the redux store when unmounting editor
  useEffect(() => {
    return () => {
      dispatch<ResetStoreAction>({
        type: RESET_STORE
      });
    };
  }, [dispatch]);

  useEffect(() => {
    function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth
      });
    }

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const bottomGapIncrease = 25; // Difference from original gap (40px + 12px scrollbar - 27px original)
    const naturalDrawerHeight =
      parseInt(theme.editor.editorHeaderHeightNoPx) +
      laneIds.length * parseInt(theme.editor.tightLaneHeightNoPx) +
      parseInt(theme.editor.rulerHeightNoPx) +
      bottomGapIncrease;
    setNaturalDrawerHeight(naturalDrawerHeight);
  }, [laneIds]);

  useEffect(() => {
    snapEditorDrawerToTop(SCREEN_COVERAGE_SNAP_PROPORTION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturalDrawerHeight, dimensions]);

  // this hook supports a generalized notification system triggered by the redux store
  // to display a notification to the user from anywhere (error, success, etc.) call dispatch<SetAlertAction>
  // show any alerts pending in the editor UI redux store
  useEffect(() => {
    if (alert) {
      closeMessage();
      showMessage({
        type: alert.role,
        duration: 5000,
        title: alert.title,
        description: alert.message
      });

      dispatch<ClearAlertAction>({
        type: CLEAR_ALERT
      });
    }
  }, [alert, closeMessage, showMessage, dispatch]);

  // this hook supports generalized wait/load/save/processing messages triggered by the redux store
  useEffect(() => {
    if (waitMessage) {
      waitMessageModal.open();
    } else {
      waitMessageModal.close();
    }
  }, [waitMessage, waitMessageModal]);

  const onMouseMove = e => {
    // The size of the box on the screen. ALl based on the viewport... Which might not work for the scroll..
    const boundingClientRect = e.currentTarget.getBoundingClientRect();
    const region_client_x = boundingClientRect.x;
    // based on the viewport.
    const clientX = e.clientX;
    const deltaX = clientX - region_client_x;

    const gridOffsetX = deltaX / zoomProportion;
    const secondsAtMousePosition = pixels_to_second(gridOffsetX);

    // save mouse position as songTime for use in other components
    dispatch<SetMouseTimeAction>({
      type: SET_MOUSE_TIME,
      seconds: secondsAtMousePosition
    });
  };
  return (
    <>
      <Header onExpand={toggleSnapEditorDrawerTop} />
      <Container
        className="editor"
        onDragEnter={handleDrag}
        tightLayout={editor.tightLayout}
      >
        <LeftPanelContainer className="left-panel">
          {!editor.tightLayout && (
            <div className="top-bar">
              <SnapControls />
              <ZoomControls />
            </div>
          )}
          <LaneHeaders />
          <AddLane addLaneModal={addLaneModal} />
          <AddLaneModal modalState={addLaneModal} />
        </LeftPanelContainer>
        <LanesAreaContainer
          id="lanes-area-container"
          className={className}
          onScroll={onScroll}
          backgroundSize={inBetweenBarLines}
        >
          <GridContainer
            id="grid-container"
            backgroundSize={inBetweenBarLines}
            initialBeatOffset={zoomInitialOffset}
            onMouseMove={onMouseMove}
          >
            {!editor.tightLayout && <TimeTicks />}
            <Locator />
            <InOutMarkers />
            {laneIds.map((laneId, i) => (
              <Lane laneIndex={i} laneId={laneId} key={laneId} />
            ))}
            {inMarkerTime !== null && (
              <MarkerOverlay
                style={{
                  left: 0,
                  width: `${seconds_to_pixels(inMarkerTime) *
                    zoomProportion}px`,
                  height: "100%"
                }}
              />
            )}

            {outMarkerTime !== null && (
              <MarkerOverlay
                style={{
                  left: `${seconds_to_pixels(outMarkerTime) *
                    zoomProportion}px`,
                  width: "100%",
                  height: "100%"
                }}
              />
            )}
            {editor.tightLayout && <TimeTicks />}
          </GridContainer>
        </LanesAreaContainer>
        {dragActive && (
          <div
            id="drag-overlay"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          ></div>
        )}
        <DragDropAudioOverlay show={dragActive && !addLaneModal.visible} />
        <WaitMessageOverlay
          show={waitMessageModal.visible}
          message={waitMessage}
        />
      </Container>
    </>
  );
};
